import {
  Sheet,
  SheetContent,
  SheetDragHandle,
  useScrollAwareSheetDrag,
} from "@/components/ui/sheet";
import { useCloseOnHistoryBack } from "@/hooks/useCloseOnHistoryBack";
import { cn } from "@/lib/utils";
import { type ReactNode } from "react";

type MobileSheetProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly children: ReactNode;
  /**
   * Tailwind class controlling sheet height. Defaults to nearly full screen.
   * Use `h-[80dvh]` for shorter sheets (selectors), `h-[92dvh]` for content-heavy ones.
   */
  readonly heightClass?: string;
  readonly className?: string;
  /** For content that carries its own close action, so it is not doubled. */
  readonly hideCloseButton?: boolean;
};

/**
 * iOS-style mobile bottom sheet: rounded top, drag-to-close handle,
 * closed by the back gesture too, soft shadow and safe-area-aware bottom
 * padding. Wraps the base Sheet
 * primitive with the styling defaults used across the company surfaces.
 */
export function MobileSheet({
  open,
  onOpenChange,
  children,
  heightClass = "h-[92dvh]",
  className,
  hideCloseButton = false,
}: MobileSheetProps) {
  const dragRef = useScrollAwareSheetDrag({
    onClose: () => onOpenChange(false),
  });
  // The phone's back gesture closes the sheet rather than leaving the page.
  useCloseOnHistoryBack(open, () => onOpenChange(false));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        hideCloseButton={hideCloseButton}
        className={cn(
          heightClass,
          "p-0 flex flex-col gap-0 rounded-t-3xl border-0 shadow-2xl",
          className,
        )}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <SheetDragHandle onClose={() => onOpenChange(false)} />
        <div ref={dragRef} className="flex flex-col flex-1 min-h-0">
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}
